// ADR-236 §5 — the tenant has no bindable AI credential. Every AI call made
// without an entitlement fails closed with HTTP 403
// {"code":"ai_credential_not_entitled"}. The API client records that here
// (from ANY call, not just chat) so the chat surface can show the same
// "AI is not set up" notice a pre-send check would have shown, instead of a
// generic error bubble. Deliberately dependency-free: services/api/client.ts
// imports it, and must not pull feature code or authStore (circular).
//
// The signal is NOT permanent (review F2) and is as NARROW as the server says:
//  - every denial expires after AI_NOT_ENTITLED_TTL_MS (the store prunes
//    itself so subscribers re-render);
//  - the 403 body / SSE error event carries "provider" and/or "model" when
//    known (internal/api/v1/ai/credential_not_entitled_scope.go). A denial
//    naming a model is scoped to that model (Direct chat never names a
//    provider — second-pass review #1); one naming only a provider is scoped
//    to the provider; one naming neither is tenant-wide;
//  - a successful Direct-chat turn or completion/embedding call clears the
//    tenant-wide denial (clearTenantWide), and an accepted offer clears
//    everything (clear).
import { create } from 'zustand'
import { registerClientStateReset } from '@/lib/queryClient'

export const AI_NOT_ENTITLED_CODE = 'ai_credential_not_entitled'

/** How long one not-entitled 403 keeps the UI in its fail-closed state. */
export const AI_NOT_ENTITLED_TTL_MS = 60_000

/** What a refusal names. Either field may be absent. */
export interface NotEntitledScope {
  provider?: string
  model?: string
}

interface AIEntitlementState {
  /** ms timestamp of the most recent TENANT-WIDE not-entitled 403 (no provider or model named), or null. */
  notEntitledAt: number | null
  /** provider slug -> ms timestamp of the most recent refusal naming only that provider. */
  deniedProviders: Record<string, number>
  /** `${provider|'*'}|${model}` -> ms timestamp of the most recent refusal naming that model. */
  deniedModels: Record<string, number>
  /**
   * Whether the server's routable set (GET /tenant/ai/offers `routable`) had
   * anything in it, as last read; null = unknown. Lets the chat say "this
   * model isn't on your rate card" rather than "AI is not set up".
   */
  hasRoutable: boolean | null
  /** Record a refusal. A bare string is a provider (back-compat). */
  markNotEntitled: (scope?: string | NotEntitledScope) => void
  /** A successful AI call: the tenant evidently has a credential. Scoped denials stay. */
  clearTenantWide: () => void
  setHasRoutable: (v: boolean | null) => void
  /** An accepted offer (or a test reset): forget every denial. hasRoutable is a fact about the set, not a denial, and is kept. */
  clear: () => void
}

type EntitlementSnapshot = Pick<AIEntitlementState, 'notEntitledAt' | 'deniedProviders'> &
  Partial<Pick<AIEntitlementState, 'deniedModels'>>

function fresh(at: number | null | undefined, now: number): boolean {
  return at != null && now - at < AI_NOT_ENTITLED_TTL_MS
}

function keepFresh(m: Record<string, number>, now: number): [Record<string, number>, number] {
  const kept: Record<string, number> = {}
  let oldest = Infinity
  for (const [k, at] of Object.entries(m)) {
    if (fresh(at, now)) {
      kept[k] = at
      oldest = Math.min(oldest, at)
    }
  }
  return [kept, oldest]
}

const modelKey = (provider: string | undefined, model: string) => `${provider || '*'}|${model}`

let pruneTimer: ReturnType<typeof setTimeout> | null = null

export const useAIEntitlementStore = create<AIEntitlementState>()((set, get) => {
  const prune = () => {
    pruneTimer = null
    const now = Date.now()
    const { notEntitledAt, deniedProviders, deniedModels } = get()
    const [providers, oldestP] = keepFresh(deniedProviders, now)
    const [models, oldestM] = keepFresh(deniedModels, now)
    const tenantWide = fresh(notEntitledAt, now) ? notEntitledAt : null
    let next = Math.min(oldestP, oldestM)
    if (tenantWide != null) next = Math.min(next, tenantWide)
    set({ notEntitledAt: tenantWide, deniedProviders: providers, deniedModels: models })
    if (next !== Infinity) schedule(next + AI_NOT_ENTITLED_TTL_MS - now)
  }
  const schedule = (delay: number) => {
    if (pruneTimer != null) clearTimeout(pruneTimer)
    pruneTimer = setTimeout(prune, Math.max(0, delay) + 1)
  }
  return {
    notEntitledAt: null,
    deniedProviders: {},
    deniedModels: {},
    hasRoutable: null,
    markNotEntitled: (arg) => {
      const scope: NotEntitledScope = typeof arg === 'string' ? { provider: arg } : (arg ?? {})
      const now = Date.now()
      if (scope.model) {
        const k = modelKey(scope.provider, scope.model)
        set((s) => ({ deniedModels: { ...s.deniedModels, [k]: now } }))
      } else if (scope.provider) {
        const p = scope.provider
        set((s) => ({ deniedProviders: { ...s.deniedProviders, [p]: now } }))
      } else {
        set({ notEntitledAt: now })
      }
      if (pruneTimer == null) schedule(AI_NOT_ENTITLED_TTL_MS)
    },
    clearTenantWide: () => {
      if (get().notEntitledAt != null) set({ notEntitledAt: null })
    },
    setHasRoutable: (v) => {
      if (get().hasRoutable !== v) set({ hasRoutable: v })
    },
    clear: () => {
      if (pruneTimer != null) clearTimeout(pruneTimer)
      pruneTimer = null
      set({ notEntitledAt: null, deniedProviders: {}, deniedModels: {} })
    },
  }
})

// Denials are per tenant/credential: drop them on any identity/tenant change.
registerClientStateReset(() => useAIEntitlementStore.getState().clear(), 'aiEntitlement')

/** True while a tenant-wide not-entitled 403 is within its TTL. */
export function isTenantWideDenied(s: EntitlementSnapshot, now: number = Date.now()): boolean {
  return fresh(s.notEntitledAt, now)
}

/** True while a not-entitled 403 naming only this provider is within its TTL. */
export function isProviderDenied(s: EntitlementSnapshot, provider: string, now: number = Date.now()): boolean {
  return fresh(s.deniedProviders[provider], now)
}

/** True while a refusal covering (provider, model) — by model, pair or provider — is within its TTL. */
export function isModelDenied(s: EntitlementSnapshot, provider: string, model: string, now: number = Date.now()): boolean {
  const models = s.deniedModels ?? {}
  return (
    fresh(models[modelKey(undefined, model)], now) ||
    fresh(models[modelKey(provider, model)], now) ||
    isProviderDenied(s, provider, now)
  )
}

/** True while any provider- or model-scoped not-entitled 403 is within its TTL. */
export function hasFreshProviderDenial(s: EntitlementSnapshot, now: number = Date.now()): boolean {
  return (
    Object.values(s.deniedProviders).some((at) => fresh(at, now)) ||
    Object.values(s.deniedModels ?? {}).some((at) => fresh(at, now))
  )
}

/** True for an HTTP 403 whose JSON body carries code ai_credential_not_entitled. */
export function isAINotEntitled(status: number, body: unknown): boolean {
  return (
    status === 403 &&
    !!body &&
    typeof body === 'object' &&
    (body as { code?: unknown }).code === AI_NOT_ENTITLED_CODE
  )
}

function stringField(body: unknown, key: string): string | undefined {
  if (!body || typeof body !== 'object') return undefined
  const v = (body as Record<string, unknown>)[key]
  return typeof v === 'string' && v !== '' ? v : undefined
}

/** The provider a not-entitled 403 body names, if any. */
export function notEntitledProviderFromBody(body: unknown): string | undefined {
  return stringField(body, 'provider')
}

/** The provider and model a not-entitled 403 body or SSE error event names. */
export function notEntitledScopeFromBody(body: unknown): NotEntitledScope {
  return { provider: stringField(body, 'provider'), model: stringField(body, 'model') }
}

/** Refusal classes (internal/domain/ai/assignment_contract.go DeniedClass*). */
export type NotEntitledClass = 'no_entitlement' | 'resolution_error' | 'misconfigured' | 'unbound'

/** The refusal class a 403 body / SSE error event carries; absent = no_entitlement. */
export function notEntitledClassFromBody(body: unknown): NotEntitledClass {
  const c = stringField(body, 'class')
  return c === 'resolution_error' || c === 'misconfigured' || c === 'unbound' ? c : 'no_entitlement'
}

/**
 * One-shot POST endpoints that invoke a model and whose 2xx proves the tenant
 * has a bindable credential. /tenant/ai/chat is deliberately absent (review
 * #5): the route also serves workflow/agent/Airflow chat, and its 200 is sent
 * before the model is bound — useChatStream clears on a Direct-chat 'done'.
 */
const AI_INVOCATION_PATH = /\/tenant\/ai\/(completion|embedding)(\?|$)/

export function isAIInvocationEndpoint(method: string | undefined, url: string): boolean {
  return (method ?? 'GET').toUpperCase() === 'POST' && AI_INVOCATION_PATH.test(url)
}
